import React from 'react'
import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { SEVEN_DAYS_AFTER_PURCHASE, FOURTEEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'

interface Props {
  /** Days before check-in/departure for credit-only and non-refundable thresholds (e.g. 21 or 45). */
  days: number
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
}

function FlexibleCancellationPolicyModalWithoutDates(props: Props) {
  const { days, isCharter } = props
  const intl = useIntl()

  return (
    <Group direction="vertical" gap={8}>
      <div>
        <BodyText variant="medium" weight="bold" colour="highlight-secondary">
          {intl.formatMessage({
            id: 'IPvnv3wz6i',
            defaultMessage: 'Full refund',
            description: 'Section heading for full refund in no-dates cancellation modal',
          })}
        </BodyText>
        <Group direction="vertical" gap={8}>
          <BodyText variant="medium" as="p">
            {intl.formatMessage({
              id: 'liUyoXv/Xx',
              defaultMessage: 'You can cancel within {days} days of purchase for a full refund to your original payment method or credit.',
              description: 'Full refund body in no-dates cancellation modal',
            }, { days: SEVEN_DAYS_AFTER_PURCHASE })}
          </BodyText>
          <div>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: '1ws1q6SyiR',
                defaultMessage: 'Conditions apply:',
                description: 'Conditions subsection heading',
              })}
            </BodyText>
            <BodyText variant="medium">
              {isCharter && intl.formatMessage({
                id: 'z8vsa6eXyh',
                defaultMessage: 'The cancellation must be made at least {days} days before departure.',
                description: 'Full refund condition text for charter',
              }, { days: FOURTEEN_DAYS_BEFORE_CHECK_IN })}
              {!isCharter && intl.formatMessage({
                id: 'vHEbbU7kfS',
                defaultMessage: 'The cancellation must be made at least {days} days before check-in.',
                description: 'Full refund condition text for non-charter',
              }, { days: FOURTEEN_DAYS_BEFORE_CHECK_IN })}
            </BodyText>
          </div>
        </Group>
      </div>

      <Hr size="md" />

      {!isCharter && (
        <div>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'ZFTrZM5XLo',
              defaultMessage: 'Full refund to credit',
              description: 'Section heading for partial/credit refund in no-dates cancellation modal',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'FZZUOcJHkh',
              defaultMessage: 'If you cancel at least {days} days before check-in, you can receive 100% refund to credit.',
              description: 'Credit-only refund for non-charter',
            }, { days })}
          </BodyText>
        </div>
      )}

      {isCharter && (
        <div>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'UhreU97i+p',
              defaultMessage: 'Partial refund',
              description: 'Section heading for partial/credit refund',
            })}
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'Ke5udm+SsF',
                defaultMessage: 'If you cancel at least {days} days before departure, a cancellation fee will apply:',
                description: 'Partial refund intro for charter in no-dates modal',
              }, { days })}
            </BodyText>
            <FlexibleCancellationCharterFlightFees />
          </Group>
        </div>
      )}

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'VHSzvk3i+O',
            defaultMessage: 'No refund',
            description: 'Section heading for no-refund period',
          })}
        </BodyText>
        <Group direction="vertical" gap={8}>
          <BodyText variant="medium">
            {isCharter && intl.formatMessage({
              id: '1HS/7d7oJ0',
              defaultMessage: 'Cancellations made within {days} days of departure are non-refundable.',
              description: 'No refund body for charter',
            }, { days })}
            {!isCharter && intl.formatMessage({
              id: 'nS9zH08wuk',
              defaultMessage: 'Cancellations made within {days} days of check-in are non-refundable.',
              description: 'No refund body for non-charter',
            }, { days })}
          </BodyText>
          <BodyText variant="medium">
            {isCharter && intl.formatMessage({
              id: 'lO9Ctg1dfd',
              defaultMessage: 'However, you can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before departure.',
              description: 'Exception for full refund within 7 days and 14 before departure',
            }, {
              purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
              beforeDepartureDays: FOURTEEN_DAYS_BEFORE_CHECK_IN,
            })}
            {!isCharter && intl.formatMessage({
              id: '2WVLC45Esw',
              defaultMessage: 'However, you can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before check-in.',
              description: 'Exception for full refund within 7 days and 14 before check-in',
            }, {
              purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
              beforeDepartureDays: FOURTEEN_DAYS_BEFORE_CHECK_IN,
            })}
          </BodyText>
        </Group>
      </div>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'UoXdYPy90j',
            defaultMessage: 'Date changes',
            description: 'Section heading for date changes',
          })}
        </BodyText>
        <BodyText variant="medium">
          {isCharter && intl.formatMessage({
            id: 'ueFqq8ZcG9',
            defaultMessage: 'One change permitted if made more than {days} days before departure. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text for charter',
          }, { days })}
          {!isCharter && intl.formatMessage({
            id: '9FQA8ZzQzI',
            defaultMessage: 'One change permitted if made more than {days} days before check-in. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text for non-charter',
          }, { days })}
        </BodyText>
      </div>
    </Group>
  )
}

export default FlexibleCancellationPolicyModalWithoutDates
